import { useState, useEffect } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const inputCls = "w-full border border-gray-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]";
const labelCls = "block text-sm font-semibold text-gray-700 mb-1";

export default function TemplateFormDialog({ open, onOpenChange, template, docTypes, mergeFields, onSaved }) {
  const isEdit = Boolean(template);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [docType, setDocType] = useState("formal_document");
  const [body, setBody] = useState("");
  const [saving, setSaving] = useState(false);
  const [cursor, setCursor] = useState(null);

  useEffect(() => {
    if (!open) return;
    setName(template?.name || "");
    setDescription(template?.description || "");
    setDocType(template?.doc_type || "formal_document");
    setBody(template?.body || "");
  }, [open, template]);

  const insertField = (key) => {
    const token = `{{${key}}}`;
    if (cursor == null) { setBody((b) => b + token); return; }
    setBody((b) => b.slice(0, cursor) + token + b.slice(cursor));
  };

  const submit = async () => {
    if (!name.trim()) { toast.error("Template name is required"); return; }
    setSaving(true);
    try {
      const payload = { name, description, doc_type: docType, body };
      if (isEdit) await api.put(`/document-templates/${template.id}`, payload);
      else await api.post("/document-templates", payload);
      toast.success(isEdit ? "Template updated" : "Template created");
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save template");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl rounded-sm max-h-[90vh] overflow-y-auto" data-testid="template-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold tracking-tight">{isEdit ? "Edit template" : "New template"}</DialogTitle>
          <DialogDescription className="sr-only">Create a reusable document template with merge fields.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-1">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Template name</label>
              <input data-testid="template-name-input" value={name} onChange={(e) => setName(e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Category</label>
              <select data-testid="template-doctype-select" value={docType} onChange={(e) => setDocType(e.target.value)} className={inputCls}>
                {Object.entries(docTypes || {}).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className={labelCls}>Description</label>
            <input data-testid="template-description-input" value={description} onChange={(e) => setDescription(e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Body</label>
            <p className="text-xs text-gray-400 mb-1.5">Click a field to insert a placeholder. Placeholders like <code className="bg-gray-100 px-1 rounded-sm">{"{{client_name}}"}</code> are filled in when you generate for a client.</p>
            <div className="flex flex-wrap gap-1.5 mb-2" data-testid="merge-field-chips">
              {(mergeFields || []).map((f) => (
                <button key={f.key} type="button" onClick={() => insertField(f.key)}
                  className="text-xs font-medium bg-[#002FA7]/10 text-[#002FA7] px-2 py-0.5 rounded-sm hover:bg-[#002FA7]/20 transition-colors">
                  {f.label}
                </button>
              ))}
            </div>
            <textarea data-testid="template-body-input" value={body} onChange={(e) => setBody(e.target.value)}
              onSelect={(e) => setCursor(e.target.selectionStart)}
              rows={12} className={`${inputCls} font-mono text-xs leading-relaxed`} />
          </div>
        </div>
        <DialogFooter className="pt-2">
          <button onClick={() => onOpenChange(false)} className="bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 px-4 py-2 rounded-sm font-semibold text-sm">Cancel</button>
          <button onClick={submit} disabled={saving} data-testid="template-save-button"
            className="bg-[#002FA7] hover:bg-[#002480] text-white px-4 py-2 rounded-sm font-semibold text-sm flex items-center gap-2 disabled:opacity-60">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} {isEdit ? "Save changes" : "Create template"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
